import { useEffect, useRef, useState } from 'react';
import { MAX_QUEUE_LENGTH, parseStoryList, type QueuedStory } from '@planning-session/shared';
import { CloseIcon } from './ui/Icon.js';
import styles from './StoryQueue.module.css';

export interface StoryQueueProps {
  readonly queue: readonly QueuedStory[];
  readonly isHost: boolean;
  onQueue: (titles: string[]) => void;
  onRemove: (storyId: string) => void;
}

/**
 * What the session is going to size next. Deliberately lighter chrome than the
 * record below it — the record is what a facilitator glances at mid-meeting, and
 * two panels of equal weight in one column would leave neither leading.
 */
export function StoryQueue({ queue, isHost, onQueue, onRemove }: StoryQueueProps) {
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState('');
  const [dropped, setDropped] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (composing) textareaRef.current?.focus();
  }, [composing]);

  const remaining = MAX_QUEUE_LENGTH - queue.length;

  // Nothing queued and no way to queue anything: the section has nothing to say.
  if (queue.length === 0 && !isHost) return null;

  function close() {
    setComposing(false);
    setDraft('');
  }

  function commit() {
    const titles = parseStoryList(draft);
    if (titles.length === 0) {
      close();
      return;
    }

    // Truncation is reported rather than silent — a host who pasted 60 stories
    // needs to know which ones did not make it.
    setDropped(Math.max(0, titles.length - remaining));
    onQueue(titles.slice(0, remaining));
    close();
  }

  return (
    <section className={styles.queue} aria-label="Story queue">
      <header className={styles.head}>
        <h2 className={styles.title}>Up next</h2>
        {queue.length > 0 && <span className={styles.count}>{queue.length}</span>}
      </header>

      {queue.length === 0 ? (
        <p className={styles.empty}>Paste your backlog and the session will work down the list.</p>
      ) : (
        <ol className={styles.list}>
          {queue.map((story, index) => (
            <li key={story.id} className={styles.row}>
              <span className={styles.ordinal}>{index + 1}</span>
              <span className={styles.storyTitle}>{story.title}</span>
              {isHost && (
                <button
                  type="button"
                  className={styles.remove}
                  onClick={() => onRemove(story.id)}
                  aria-label={`Remove ${story.title} from the queue`}
                >
                  <CloseIcon size={14} />
                </button>
              )}
            </li>
          ))}
        </ol>
      )}

      {isHost &&
        (composing ? (
          <div className={styles.composer}>
            <textarea
              ref={textareaRef}
              className={styles.textarea}
              value={draft}
              rows={4}
              placeholder={'One story per line\nAUTH-14 Rotate refresh tokens'}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  close();
                } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                  commit();
                }
              }}
            />
            <div className={styles.composerActions}>
              <button type="button" className={styles.ghost} onClick={close}>
                Cancel
              </button>
              <button type="button" className={styles.add} onClick={commit} disabled={draft.trim().length === 0}>
                Add to queue
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className={styles.ghost} onClick={() => setComposing(true)} disabled={remaining === 0}>
            {remaining === 0 ? 'Queue is full' : 'Paste a list of stories'}
          </button>
        ))}

      {dropped > 0 && (
        <p className={styles.dropped} role="status">
          {dropped} {dropped === 1 ? 'story' : 'stories'} did not fit — the queue holds {MAX_QUEUE_LENGTH}.
        </p>
      )}
    </section>
  );
}
